import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { formsActions } from '../forms-actions';
import { catchError, combineLatestWith, exhaustMap, filter, map, mergeMap, of, tap } from 'rxjs';
import { selectForm, selectFormField } from '../forms-selectors';
import { Store } from '@ngrx/store';
import { LookupService } from 'src/app/shared/services/lookup/lookup.service';
import { CollectionFormLookupApiService } from '../../../../api/bizzmine/collection-form-lookup/collection-form-lookup-api.service';
import { CollectionFormFieldGridLookupData } from '../../../../../models/ts/collection-form-field-grid-lookup-data.model';
import { UpdateDeleteState } from '../../../../../models/ts/update-delete-state.model';
import { ViewDataSourcesInstance } from '../../../../../models/ts/view-data-sources-instance.model';
import { concatLatestFrom } from '@ngrx/operators';
import { CollectionFormLinkedService } from '../../../../features/bizzmine/form/services/collection-form-linked.service';
import { userSettingsFeature } from '../../user-settings/user-settings-feature';
import { ChecklistApiService } from 'src/app/api/bizzmine/checklist/checklist.service';

/**
 * Effects specific to 1xN grid controls
 */
@Injectable()
export class FormsGridEffects {
  private actions$ = inject(Actions);
  private collectionFormLinkedService = inject(CollectionFormLinkedService);

  public emptyRowAddedToGrid$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.addRowsToGrid),
    map(({ formId, gridFieldId }) => ({
      type: formsActions.rowsAddedToGrid.type,
      formId,
      gridFieldId
    }))
  ));
  private store$ = inject(Store);
  private lookupService = inject(LookupService);
  public getEmptyRowForGrid$ = createEffect(() => this.actions$.pipe(
      ofType(formsActions.addEmptyRowToGrid),
      concatLatestFrom((props) => [
        this.store$.select(selectFormField(props.formId, props.gridFieldId)),
        this.store$.select(selectForm(props.formId))
      ]),
      exhaustMap(([props, field, form]) => this.lookupService.getEmptyGridRecord(form!.data.CollectionFormId, structuredClone(field)).pipe(
        tap(record => {
          if (field !== undefined && form !== undefined) {
            let newId = LookupService.getNextNewRecordId(field.Records ?? []);
            record.CrossLinkedInstancesID = newId;
            record.RowDataDesignCrossID = newId;
            record.EditMode = true;
          } else throw new Error(`Field ${props.gridFieldId} and/or form ${props.formId} are undefined`);
        }),
        map(record => ({
          type: formsActions.getEmptyRowForGridSucceeded.type,
          formId: props.formId,
          gridFieldId: props.gridFieldId,
          emptyRow: record
        })),
        catchError((error: Error) => of(formsActions.getEmptyRowForGridFailed({
            formId: props.formId,
            error: error
          }))
        ))
      )
    )
  );


  private checklistApiService = inject(ChecklistApiService);
  public createComboboxValue$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.createChecklistGroupValue),
    exhaustMap(({ formId, gridFieldId, recordId, collectionFieldsId, caption }) => {
      return this.checklistApiService.addChecklistGroupValue({
        caption: caption,
        collectionFieldsId: collectionFieldsId,
        languageId: this.store$.selectSignal(userSettingsFeature.selectUserLanguagesID)()!
      }).pipe(combineLatestWith(of({ formId, gridFieldId, recordId, collectionFieldsId, caption })));
    }),
    map(([result, request]) => {
      const value = result?.find(v => v.Caption === request.caption);
      return formsActions.createComboboxValueSucceeded({
        options: result,
        formId: request.formId,
        gridFieldId: request.gridFieldId,
        recordId: request.recordId,
        fieldId: request.collectionFieldsId,
        value: value?.CollectionFieldValuesID
      });
    })
  ));

  public createComboboxValueSucceeded$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.mapComboboxValues),
    filter((value) => value != undefined && value.recordId != undefined),
    map(({ formId, gridFieldId, recordId, fieldId, value }) => {
      return formsActions.updateGridFormFieldValue({
        formId: formId,
        gridFieldId: gridFieldId,
        recordId: recordId!,
        recordFieldId: fieldId,
        value: value
      });
    })
  ));

  public mapComboboxValues$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.createComboboxValueSucceeded),
    map((data) => {
      return formsActions.mapComboboxValues(data);
    })
  ));

  private addEmptyRowToGrid$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.getEmptyRowForGridSucceeded),
    map(props => ({
      type: formsActions.addRowsToGrid.type,
      formId: props.formId,
      gridFieldId: props.gridFieldId,
      records: [props.emptyRow]
    }))
  ));
  private collectionFormLookupApiService = inject(CollectionFormLookupApiService);

  public addViewDataInstancesToGrid$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.addViewDataInstancesToGrid),
    mergeMap(data => {
      const form = this.store$.selectSignal(selectForm(data.formId))();
      if (form == null) {
        throw new Error(`Form with id ${data.formId} not found`);
      }
      return this.collectionFormLinkedService.getGridRecordLookupBulk(data.formId, form.data, data.viewDataSourceId, data.request.map(_ => {
        return {
          instanceId: _.instanceId, versionId: _.instanceId, crossLinkedInstanceId: _.crossLinkInstancesId ?? 0,
          originalChildInstancesId: _.originalChildInstancesId,
          parentInstanceId: _.parentInstanceId
        };
      })).pipe(combineLatestWith(of(data)));
    }),
    tap(([found, request]) => {
      if (found != null) {
        found.Instances.forEach((lookupData, index) => {
          const form = this.store$.selectSignal(selectForm(request.formId))();
          if (form == null) {
            throw new Error(`Form with id ${request.formId} not found`);
          }
          const lookupMultiple = lookupData as CollectionFormFieldGridLookupData;
          let rowId = -1;
          const field = this.store$.selectSignal(selectFormField(request.formId, request.targetGridField.Id))();
          if (field?.Records != null && field.Records.length > 0) {
            const lowestIdFound = Math.min(...(field.Records.map(r => r.RowDataDesignCrossID)));
            if (lowestIdFound < 0) {
              rowId = lowestIdFound - 1;
            }
          }
          this.store$.dispatch(formsActions.addRowsToGrid({
            formId: request.formId,
            gridFieldId: request.targetGridField.Id,
            records: [{
              Fields: lookupMultiple.FormFields,
              State: UpdateDeleteState.Update,
              CrossLinkedInstancesID: rowId,
              RowDataDesignCrossID: rowId,
              EditMode: false
            }]
          }));

          const vds = form.data.ViewDataSources.find(vds => vds.ViewDataSourcesID === request.viewDataSourceId);
          if (vds != null) {
            const clonedVds = structuredClone(vds);
            clonedVds.Instances.push({
              OriginalChildInstancesID: lookupData.Request.OriginalChildInstancesID,
              ChildVersionsID: lookupData.Request.VersionsID,
              ChildInstancesID: lookupData.Request.InstancesID,
              State: UpdateDeleteState.Update,
              CrossLinkedInstancesID: rowId,
              RowDataDesignCrossID: rowId
            } as ViewDataSourcesInstance);
            this.store$.dispatch(formsActions.updateFormViewDataSources({
              formId: request.formId,
              viewDataSources: [clonedVds]
            }));
          }
        });
      }
    })
  ), { dispatch: false });

}